/**
 * The invoice, drawn.
 *
 * A PDF and not a web page, which is the opposite of every other document this
 * app sends. A proposal is a link because a link can be signed and can record
 * that somebody opened it; a bill is a file because the person paying it drops
 * it into an accounts system that wants an attachment. Jonathon, 2026-08-27:
 * "vendors ask for pdf invoices that we can send that will trigger them to pay
 * us."
 *
 * Drawn in the browser rather than on a server because nothing on the server
 * can make a PDF — the storm report says so in as many words and sends HTML
 * instead. This is the first thing here that genuinely has to be one, so it
 * carries the only dependency that can do it, loaded on demand so that the app
 * a rep opens on a driveway does not download a PDF engine to look at a job.
 *
 * The layout is the one on the invoice Jonathon sent as the example: title top
 * left, mark top right, three columns of who-and-what, the items, the total,
 * and the terms underneath. Nothing here computes money — every figure arrives
 * on the snapshot, worked out by `packages/core/src/invoice.ts`.
 */

import {
  invoiceDate,
  invoiceMoney,
  invoiceQty,
  type Cents,
  type InvoiceLine,
  type InvoiceSnapshot,
} from "@roof-hawk/core";

import { COMPANY_LOGO_URL } from "../logo";

/** Letter, in points, which is what jsPDF measures in. */
const PAGE = { width: 612, height: 792 };
const MARGIN = 54;
const RIGHT = PAGE.width - 56;

/** Where each column of the items table sits. Right edges, for numbers. */
const COLUMN = {
  item: MARGIN,
  itemWidth: 200,
  qty: 330,
  unitPrice: 440,
  total: RIGHT,
};

const INK = "#111111";
const GREY = "#6b7280";
const RULE = "#d4d4d8";

/** How far down a new page starts its rows. */
const ROWS_TOP = 120;
/** The last y a row may start at before it has to go on the next page. */
const ROWS_BOTTOM = PAGE.height - 140;

type Doc = import("jspdf").jsPDF;

/**
 * The company's mark, as bytes a PDF can hold.
 *
 * Fetched rather than imported: it is a file in `public/`, it changes when the
 * company rebrands (CLAUDE.md rule 11), and a build-time import would freeze a
 * copy of it into the bundle. A failure to load it is not a failure to invoice
 * — the bill goes out without the badge rather than not at all.
 */
async function logoDataUrl(url = COMPANY_LOGO_URL): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** A label in the small grey type the headings use. */
function heading(doc: Doc, text: string, x: number, y: number): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(GREY);
  doc.text(text, x, y);
  doc.setTextColor(INK);
}

function rule(doc: Doc, y: number, from = MARGIN, to = RIGHT): void {
  doc.setDrawColor(RULE);
  doc.setLineWidth(0.7);
  doc.line(from, y, to, y);
}

/** The three columns under From / To / Details. */
function party(
  doc: Doc,
  lines: readonly string[],
  x: number,
  top: number,
): number {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  let y = top;
  for (const line of lines) {
    if (!line) {
      y += 8;
      continue;
    }
    doc.text(line, x, y);
    y += 15;
  }
  return y;
}

/** The header row over the items, drawn once per page. */
function itemsHeader(doc: Doc, y: number): number {
  heading(doc, "Item", COLUMN.item, y);
  doc.setTextColor(GREY);
  doc.text("Quantity", COLUMN.qty, y, { align: "right" });
  doc.text("Unit price", COLUMN.unitPrice, y, { align: "right" });
  doc.text("Total", COLUMN.total, y, { align: "right" });
  doc.setTextColor(INK);
  rule(doc, y + 10);
  return y + 32;
}

/**
 * The estimate a run of lines came from, over the top of them.
 *
 * Only drawn on a bill that covers more than one — `invoiceGroupLines` sets
 * the group then, and leaves it off otherwise. Without it a customer billed
 * for two of the options they signed reads one undivided list and cannot tell
 * which line belongs to which roof.
 */
function groupHeading(doc: Doc, label: string, y: number): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  doc.text(label, COLUMN.item, y);
  doc.setFont("helvetica", "normal");
  return y + 20;
}

/** One line of the bill. Returns the y the next one starts at. */
function itemRow(doc: Doc, line: InvoiceLine, y: number): number {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);

  const wrapped = doc.splitTextToSize(line.description, COLUMN.itemWidth);
  doc.text(wrapped, COLUMN.item, y);
  doc.text(invoiceQty(line.qty), COLUMN.qty, y, { align: "right" });
  doc.text(invoiceMoney(line.unitPrice), COLUMN.unitPrice, y, {
    align: "right",
  });
  doc.text(invoiceMoney(line.amount), COLUMN.total, y, { align: "right" });

  const height = Math.max(wrapped.length, 1) * 14 + 12;
  rule(doc, y + height - 12);
  return y + height;
}

/**
 * Draw the whole thing.
 *
 * Exported for the test, which reads the PDF back rather than looking at it.
 */
export async function renderInvoice(
  snapshot: InvoiceSnapshot,
  options: { logo?: string | null } = {},
): Promise<Doc> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    // Uncompressed, so a test can read the words back out of the file and so
    // a carrier's document scanner has nothing to fail at.
    compress: false,
  });

  doc.setProperties({
    title: `Invoice ${snapshot.number}`,
    author: snapshot.from.name,
    subject: `Invoice ${snapshot.number}`,
  });

  // ------------------------------------------------------------- the head ---

  doc.setFont("helvetica", "normal");
  doc.setFontSize(40);
  doc.setTextColor(INK);
  doc.text("Invoice", MARGIN, 100);

  const logo = options.logo === undefined ? await logoDataUrl() : options.logo;
  if (logo) {
    try {
      doc.addImage(logo, "PNG", RIGHT - 60, 52, 60, 60);
    } catch {
      // A mark that will not draw is not a reason to withhold a bill.
    }
  }

  heading(doc, "From", MARGIN, 160);
  heading(doc, "To", 272, 160);
  heading(doc, "Details", 461, 160);
  rule(doc, 172);

  party(
    doc,
    [
      snapshot.from.name,
      snapshot.from.email ?? "",
      "",
      ...snapshot.from.address,
    ],
    MARGIN,
    196,
  );
  party(
    doc,
    [
      snapshot.billTo.name,
      snapshot.billTo.email ?? "",
      "",
      ...snapshot.billTo.address,
    ],
    272,
    196,
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.text("Invoice no.", 461, 196);
  doc.text(snapshot.number, COLUMN.total, 196, { align: "right" });

  // ------------------------------------------------------------ the items ---

  let y = itemsHeader(doc, 330);
  let group: string | null = null;
  for (const line of snapshot.lines) {
    if (y > ROWS_BOTTOM) {
      doc.addPage();
      y = itemsHeader(doc, ROWS_TOP);
      // The heading again at the top of the page, so a second sheet of lines
      // is not orphaned from the estimate it belongs to.
      group = null;
    }
    if (line.group && line.group !== group) {
      group = line.group;
      y = groupHeading(doc, group, y);
    }
    y = itemRow(doc, line, y);
  }

  if (y > ROWS_BOTTOM - 40) {
    doc.addPage();
    y = ROWS_TOP;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(20);
  doc.text("Total", 272, y + 28);
  doc.text(invoiceMoney(snapshot.total), COLUMN.total, y + 28, {
    align: "right",
  });
  y += 28;

  /*
   * What has been paid, and what is left.
   *
   * Jonathon, 2026-09-22: an invoice "needs to show the amount received and
   * the remaining due." Both figures arrive on the snapshot from `jobBalance`
   * — the one place in Roof Board that answers how much is owed — and nothing
   * here subtracts, so the bill cannot disagree with the balance card, the
   * production board or the letter that chases it.
   *
   * Which means the balance is the JOB's, and on a bill covering part of a job
   * it can be more than the invoice asks for. That is said in words underneath
   * rather than quietly shown as a smaller number: a customer told they owe
   * less than they do is the fault this figure exists to prevent.
   */
  if (snapshot.received != null || snapshot.balanceDue != null) {
    doc.setFontSize(11);
    doc.setTextColor(GREY);
    doc.text("Received to date", 272, y + 24);
    doc.text("Balance due", 272, y + 42);
    doc.setTextColor(INK);
    doc.text(
      invoiceMoney(snapshot.received ?? (0 as Cents)),
      COLUMN.total,
      y + 24,
      { align: "right" },
    );
    doc.setFont("helvetica", "bold");
    doc.text(
      invoiceMoney(snapshot.balanceDue ?? snapshot.total),
      COLUMN.total,
      y + 42,
      { align: "right" },
    );
    doc.setFont("helvetica", "normal");
    y += 42;

    const due = snapshot.balanceDue ?? snapshot.total;
    if (due !== snapshot.total - (snapshot.received ?? 0)) {
      doc.setFontSize(9.5);
      doc.setTextColor(GREY);
      doc.text(
        "Received and balance are for the whole job, which this invoice may " +
          "be part of.",
        272,
        y + 18,
      );
      doc.setTextColor(INK);
      y += 18;
    }
  }

  // ------------------------------------------------------------ the terms ---

  const terms = Math.min(y + 92, PAGE.height - 120);
  heading(doc, "Terms", MARGIN, terms);
  heading(doc, "Memo", 272, terms);
  rule(doc, terms + 12);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  doc.text("Invoice date", MARGIN, terms + 40);
  doc.text(invoiceDate(snapshot.invoiceDate), MARGIN + 108, terms + 40);
  doc.text("Due date", MARGIN, terms + 56);
  doc.text(invoiceDate(snapshot.dueDate), MARGIN + 108, terms + 56);

  if (snapshot.memo) {
    doc.text(doc.splitTextToSize(snapshot.memo, 230), 272, terms + 40);
  }

  return doc;
}

/** The file, for downloading or attaching. */
export async function invoicePdfBlob(
  snapshot: InvoiceSnapshot,
  options: { logo?: string | null } = {},
): Promise<Blob> {
  const doc = await renderInvoice(snapshot, options);
  return doc.output("blob");
}

/** The file as base64, which is what the Gmail Edge Function attaches. */
export async function invoicePdfBase64(
  snapshot: InvoiceSnapshot,
  options: { logo?: string | null } = {},
): Promise<string> {
  const doc = await renderInvoice(snapshot, options);
  // jsPDF's own base64 output, so no round trip through a Blob and a reader.
  return doc.output("datauristring").split(",")[1] ?? "";
}

/** Hand the file to whoever pressed the button. */
export async function downloadInvoicePdf(
  snapshot: InvoiceSnapshot,
  filename: string,
): Promise<void> {
  const doc = await renderInvoice(snapshot);
  doc.save(filename);
}
